// Mọi lời gọi API của trang Studio (SPEC Studio mục 4, 6.4–6.5, 7, 8.11) + hook tải dữ liệu, hook người xem.
//
// STUDIO_URLS là hằng URL RIÊNG của module (mục 10.4): cùng giá trị với khối "Studio" mà gói I2 thêm vào
// service/api-route.ts (phục vụ pending-counts.ts, use-current-roles.ts). Không import API cho khoá mới; hai
// nơi phải giữ cùng giá trị. Khoá có sẵn được dùng lại từ API: uploadFile, revenues, leaveRequests,
// starsMissionReviews.
// Sai quyền / ngoài phạm vi: backend trả 409 kèm mã STUDIO_* (không 401/403), xem studio-error.ts.

import { useCallback, useEffect, useRef, useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import type {
  ApprovalKind,
  ApprovalMe,
  ApprovalSummary,
  BulkResponse,
  FlagRow,
  LogRow,
  ManagerCandidate,
  Paged,
  QueueItem,
  StudioBulkResponse,
  StudioCandidate,
  StudioCreated,
  StudioFlagRow,
  StudioKcResponse,
  StudioLinkRelation,
  StudioListResponse,
  StudioLogRow,
  StudioMembersChange,
  StudioMembersResponse,
  StudioMine,
  StudioMonitorSummary,
  StudioOverview,
  StudioQueueItem,
  StudioQueueKind,
  StudioRequestKind,
  StudioRequestResult,
  StudioRequestRow,
  StudioRequestStatus,
  StudioRequestsSummary,
  StudioScopeMode,
  StudioSummary,
  StudioTeamKind,
  StudioTeamMembersResult,
  StudioTeamRow,
  StudioTeamsResponse,
  StudioViewerKind,
  FlagSeverity,
  StudioMemberBase,
} from './contract';

// ------------------------------------------------------------------ URL ---

export const STUDIO_URLS = {
  // Studio — hp-media-server-staging/src/studios (docs/studio-quan-ly/SPEC-TRIEN-KHAI-STUDIO.md).
  studiosMine: '/studios/mine',
  studios: '/studios', // GET danh sách · POST tạo
  studioManagerCandidates: '/studios/manager-candidates',
  studioCandidates: '/studios/candidates', // ?q=&studioId= — ô chọn thành viên, không có email
  studio: (id: number) => `/studios/${id}`, // GET · PATCH · DELETE · + /manager /overview /members /teams /kc /log /flags /requests
  studioRequests: '/studios/requests', // GET lịch sử · + /:id/approve /:id/reject /:id/cancel /:id/apply
  studioRequestsSummary: '/studios/requests/summary',
  studioRequestsQueue: '/studios/requests/queue',
  studioRequestsBulk: '/studios/requests/bulk',
  studioMonitorSummary: '/studios/monitor/summary',
  studioMonitorFlags: '/studios/monitor/flags',
  studioMonitorLog: '/studios/monitor/log',
  // Duyệt nhanh — hp-media-server-staging/src/approvals/api/approvals.controller.ts
  approvalsMe: '/approvals/me',
  approvalsSummary: '/approvals/summary', // ?studioId=
  approvalsQueue: '/approvals/queue', // ?kind=&studioId=&page=&limit=
  approvalsBulk: '/approvals/bulk',
  approvalsFlags: '/approvals/flags', // + /:id (PATCH khép cờ)
  approvalsLog: '/approvals/log', // + /:id/dispute
};

/** Đường dẫn trang admin (mục 8.1). */
export const STUDIO_PAGES = {
  list: '/studios',
  mine: '/studio',
  detail: (id: number, tab?: string) => (tab ? `/studio/${id}?tab=${tab}` : `/studio/${id}`),
  listTab: (tab: 'requests' | 'monitor', studioId?: number) =>
    studioId ? `/studios?tab=${tab}&studioId=${studioId}` : `/studios?tab=${tab}`,
};

/** Số dòng mặc định mỗi trang của các bảng Studio (backend mặc định 20; S10 mặc định 50). */
export const STUDIO_PAGE_LIMIT = 20;
export const STUDIO_MEMBERS_LIMIT = 50;

/** Phân trang riêng dưới bảng (mẫu waives-tab: Table pagination={false} + <Pagination>). */
export interface StudioPaging {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}

// -------------------------------------------------------------- tiện ích ---

type QueryValue = string | number | boolean | null | undefined | (string | number)[];

/** Bỏ giá trị rỗng; mảng nối bằng dấu phẩy; boolean → '1' / '0'. */
export function toQuery(params: Record<string, QueryValue>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      if (value.length) out[key] = value.join(',');
      return;
    }
    if (typeof value === 'boolean') {
      out[key] = value ? '1' : '0';
      return;
    }
    out[key] = value;
  });
  return out;
}

// AxiosUtils trả nguyên response của axios (quy ước của dự án): dữ liệu nằm ở `.data`.
async function getJson<T>(url: string, params?: Record<string, QueryValue>): Promise<T> {
  const res: any = await AxiosUtils.get(url, params ? toQuery(params) : undefined);
  return res?.data as T;
}
async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const res: any = await AxiosUtils.post(url, body ?? {});
  return res?.data as T;
}
async function patchJson<T>(url: string, body?: unknown): Promise<T> {
  const res: any = await AxiosUtils.patch(url, body ?? {});
  return res?.data as T;
}
async function putJson<T>(url: string, body?: unknown): Promise<T> {
  const res: any = await AxiosUtils.put(url, body ?? {});
  return res?.data as T;
}
async function deleteJson<T>(url: string, body?: unknown): Promise<T> {
  const res: any = await AxiosUtils._delete(url, body ?? {});
  return res?.data as T;
}

/** Lỗi phía trình duyệt, cùng dạng lỗi máy chủ để mapStudioError đọc được câu ở `error`. */
function clientError(code: string, text: string) {
  const err = new Error(code) as Error & { error: string };
  err.error = text;
  return err;
}

const idPath = (id: number, sub = '') => `${STUDIO_URLS.studio(id)}${sub}`;
const requestPath = (id: number, action: string) => `${STUDIO_URLS.studioRequests}/${id}/${action}`;

// ---------------------------------------------------- kiểu thân / truy vấn ---

export type StudioSort = 'kc_desc' | 'title' | 'created_desc';
export interface StudioListQuery {
  page?: number;
  limit?: number;
  q?: string;
  month?: string;
  sort?: StudioSort;
}
export interface CreateStudioBody {
  title: string;
  managerId?: number; // không bắt buộc: Studio chưa có Quản lý
  description?: string;
  avatar?: string;
  tiktokGroupName?: string;
  assistantIds?: number[];
  memberUserIds?: number[];
  memberUids?: string[];
  reason?: string;
}
/** `tiktokGroupName` rỗng hoặc null = bỏ gắn; đổi tên này thì `reason` bắt buộc ≥ 5 ký tự. */
export interface EditStudioBody {
  title?: string;
  description?: string;
  avatar?: string;
  tiktokGroupName?: string | null;
  assistantIds?: number[];
  reason?: string;
}
export type MemberSort = 'kc_desc' | 'name' | 'live_days_desc';
export interface StudioMembersQuery {
  month?: string;
  q?: string;
  source?: StudioMemberBase['source'];
  type?: 'CREATOR' | 'STAFF' | 'ASSISTANT';
  /** Số, hoặc 'none' = chưa xếp nhóm. */
  teamId?: number | 'none';
  /** true = chỉ người không còn trong file. */
  inactive?: boolean;
  sort?: MemberSort;
  page?: number;
  limit?: number;
}
export interface StudioTeamsQuery {
  status?: 'ACTIVE' | 'ARCHIVED' | 'ALL';
  month?: string;
}
export interface CreateTeamBody {
  name: string;
  kind?: StudioTeamKind;
  leaderKey?: string;
  memberKeys?: string[];
  note?: string;
}
/** `leaderKey: null` = bỏ Nhóm trưởng. Không có UID kênh (UID kênh đặt bằng setTeamChannel). */
export interface EditTeamBody {
  name?: string;
  leaderKey?: string | null;
  note?: string;
}
export interface AddTeamMembersBody {
  keys: string[];
  role?: 'DANCER' | 'SUPPORT';
  moveIfInOtherTeam?: boolean;
  note?: string;
}
export interface StudioKcQuery {
  base?: string;
  /** 1–3 tháng, khác `base`. */
  compare?: string[];
  by?: 'creator' | 'team';
  /** Mặc định bật (backend mặc định 1). */
  sameDay?: boolean;
  q?: string;
}
export interface StudioLogQuery {
  from?: string;
  to?: string;
  actions?: string[];
  actorId?: number;
  subjectId?: number;
  relation?: StudioLinkRelation;
  flagged?: boolean;
  page?: number;
  limit?: number;
}
export interface MonitorLogQuery extends StudioLogQuery {
  studioId?: number;
}
export type FlagStatus = 'OPEN' | 'OK' | 'ACTION';
export interface StudioFlagsQuery {
  status?: FlagStatus;
  severity?: FlagSeverity;
  month?: string;
  page?: number;
  limit?: number;
}
export interface MonitorFlagsQuery {
  studioId?: number;
  status?: FlagStatus;
  severity?: FlagSeverity;
  code?: string;
  actorId?: number;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}
/** Thân S24 theo loại đề nghị (mục 4.6). `evidenceUrl` là khoá 'hp/<uuid>' do uploadStudioImage trả. */
export type CreateStudioRequestBody =
  | { requestKind: 'MEMBER_ADD'; reason: string; evidenceUrl?: string; userId?: number; uid?: string }
  | { requestKind: 'MEMBER_REMOVE'; reason: string; evidenceUrl?: string; key: string }
  | {
      requestKind: 'KC_MANUAL';
      reason: string;
      evidenceUrl: string;
      userId: number;
      kcDate: string;
      kc: number;
    }
  | {
      requestKind: 'TEAM_CHANNEL';
      reason: string;
      evidenceUrl?: string;
      teamId: number;
      /** null = đề nghị bỏ kênh. */
      uid: string | null;
    };
export type RequestStatusQuery = StudioRequestStatus | 'UNAPPLIED' | 'ALL';
export interface StudioRequestsQuery {
  status?: RequestStatusQuery;
  requestKind?: StudioRequestKind;
  kind?: StudioQueueKind;
  page?: number;
  limit?: number;
}
export interface RequestsHistoryQuery extends StudioRequestsQuery {
  studioId?: number;
}
export interface RequestsQueueQuery {
  /** Không gửi = cả hai loại việc trong một bảng. */
  kind?: StudioQueueKind;
  studioId?: number;
  page?: number;
  limit?: number;
}
export interface ApprovalsQueueQuery {
  kind: ApprovalKind;
  studioId?: number;
  page?: number;
  limit?: number;
}

// -------------------------------------------------------- Studio (S1–S9) ---

export const getStudioMine = () => getJson<StudioMine>(STUDIO_URLS.studiosMine);

export const listStudios = (q: StudioListQuery = {}) =>
  getJson<StudioListResponse>(STUDIO_URLS.studios, { ...q });

export const getManagerCandidates = (q?: string) =>
  getJson<ManagerCandidate[]>(STUDIO_URLS.studioManagerCandidates, { q: q?.trim() });

export const createStudio = (body: CreateStudioBody) =>
  postJson<StudioCreated>(STUDIO_URLS.studios, body);

export const getStudio = (id: number) => getJson<StudioSummary>(idPath(id));

export const editStudio = (id: number, body: EditStudioBody) =>
  patchJson<StudioSummary>(idPath(id), body);

export const changeStudioManager = (id: number, body: { managerId: number; reason: string }) =>
  postJson<StudioSummary>(idPath(id, '/manager'), body);

export const deleteStudio = (id: number, reason: string) =>
  deleteJson<{ deleted: true }>(idPath(id), { reason });

export const getStudioOverview = (id: number, month?: string) =>
  getJson<StudioOverview>(idPath(id, '/overview'), { month });

/** Gợi ý "Nhóm trong file TikTok" (GET /revenues/agency-groups, chỉ ADMIN). */
export const getAgencyGroups = () => getJson<string[]>(`${API.revenues}/agency-groups`);

// ------------------------------------------------ thành viên (S10–S13, S26) ---

export const getStudioMembers = (id: number, q: StudioMembersQuery = {}) =>
  getJson<StudioMembersResponse>(idPath(id, '/members'), {
    ...q,
    inactive: q.inactive ? '1' : undefined,
  });

/** S26: ô chọn thành viên. `q` sau trim ≥ 2 ký tự; `studioId` bắt buộc với Quản lý. */
export const searchStudioCandidates = (q: string, studioId?: number) =>
  getJson<StudioCandidate[]>(STUDIO_URLS.studioCandidates, { q: q.trim(), studioId });

export const addStudioMembers = (
  id: number,
  body: { userIds?: number[]; uids?: string[]; reason?: string }
) => postJson<StudioMembersChange>(idPath(id, '/members'), body);

export const removeStudioMember = (id: number, key: string, reason: string) =>
  deleteJson<{ removed: true }>(idPath(id, `/members/${encodeURIComponent(key)}`), { reason });

export const moveStudioMember = (
  id: number,
  key: string,
  body: { targetStudioId: number; reason: string }
) => postJson<{ moved: true }>(idPath(id, `/members/${encodeURIComponent(key)}/move`), body);

// -------------------------------------------------- Nhóm nhảy (S14–S20, S27) ---

export const getStudioTeams = (id: number, q: StudioTeamsQuery = {}) =>
  getJson<StudioTeamsResponse>(idPath(id, '/teams'), { ...q });

export const createStudioTeam = (id: number, body: CreateTeamBody) =>
  postJson<StudioTeamRow>(idPath(id, '/teams'), body);

export const editStudioTeam = (id: number, teamId: number, body: EditTeamBody) =>
  patchJson<StudioTeamRow>(idPath(id, `/teams/${teamId}`), body);

/** S27 (ADMIN): `channelTiktokUid = null` = bỏ kênh. */
export const setTeamChannel = (
  id: number,
  teamId: number,
  body: { channelTiktokUid: string | null; reason: string }
) => putJson<StudioTeamRow>(idPath(id, `/teams/${teamId}/channel`), body);

export const archiveStudioTeam = (id: number, teamId: number, reason: string) =>
  postJson<StudioTeamRow>(idPath(id, `/teams/${teamId}/archive`), { reason });

export const restoreStudioTeam = (id: number, teamId: number) =>
  postJson<StudioTeamRow>(idPath(id, `/teams/${teamId}/restore`), {});

export const addTeamMembers = (id: number, teamId: number, body: AddTeamMembersBody) =>
  postJson<StudioTeamMembersResult>(idPath(id, `/teams/${teamId}/members`), body);

export const removeTeamMember = (id: number, teamId: number, memberId: number, note?: string) =>
  deleteJson<{ removed: true }>(
    idPath(id, `/teams/${teamId}/members/${memberId}`),
    note ? { note } : {}
  );

// ------------------------------------------------------- kim cương (S21) ---

export const getStudioKc = (id: number, q: StudioKcQuery = {}) =>
  getJson<StudioKcResponse>(idPath(id, '/kc'), {
    base: q.base,
    compare: q.compare,
    by: q.by,
    sameDay: q.sameDay, // boolean → '1' / '0'; không truyền = backend mặc định 1
    q: q.q,
  });

// ------------------------------------------- nhật ký, cờ, giám sát (S22, S23, S28–S30) ---

export const getStudioLog = (id: number, q: StudioLogQuery = {}) =>
  getJson<Paged<StudioLogRow>>(idPath(id, '/log'), {
    ...q,
    flagged: q.flagged ? '1' : undefined,
  });

export const getStudioFlags = (id: number, q: StudioFlagsQuery = {}) =>
  getJson<Paged<StudioFlagRow>>(idPath(id, '/flags'), { ...q });

export const getMonitorSummary = () => getJson<StudioMonitorSummary>(STUDIO_URLS.studioMonitorSummary);

export const getMonitorFlags = (q: MonitorFlagsQuery = {}) =>
  getJson<Paged<StudioFlagRow>>(STUDIO_URLS.studioMonitorFlags, { ...q });

export const getMonitorLog = (q: MonitorLogQuery = {}) =>
  getJson<Paged<StudioLogRow>>(STUDIO_URLS.studioMonitorLog, {
    ...q,
    flagged: q.flagged ? '1' : undefined,
  });

/** Admin khép cờ: Hợp lệ (OK) / Cần xử lý (ACTION), ghi chú 5–500 ký tự. */
export const closeFlag = (flagId: number, body: { status: 'OK' | 'ACTION'; note: string }) =>
  patchJson<FlagRow>(`${STUDIO_URLS.approvalsFlags}/${flagId}`, body);

/** "Báo sai" một dòng nhật ký về mình (route sẵn có). */
export const disputeLog = (logId: number, note: string) =>
  postJson<LogRow>(`${STUDIO_URLS.approvalsLog}/${logId}/dispute`, { note });

// ------------------------------------------------------ đề nghị (S24, S25, Q1–Q8) ---

export const createStudioRequest = (id: number, body: CreateStudioRequestBody) =>
  postJson<StudioRequestRow>(idPath(id, '/requests'), body);

export const getStudioRequests = (id: number, q: StudioRequestsQuery = {}) =>
  getJson<Paged<StudioRequestRow>>(idPath(id, '/requests'), { ...q });

export const getRequestsSummary = () =>
  getJson<StudioRequestsSummary>(STUDIO_URLS.studioRequestsSummary);

export const getRequestsQueue = (q: RequestsQueueQuery = {}) =>
  getJson<Paged<StudioQueueItem>>(STUDIO_URLS.studioRequestsQueue, { ...q });

export const getRequestsHistory = (q: RequestsHistoryQuery = {}) =>
  getJson<Paged<StudioRequestRow>>(STUDIO_URLS.studioRequests, { ...q });

export const cancelStudioRequest = (requestId: number, note?: string) =>
  postJson<StudioRequestResult>(requestPath(requestId, 'cancel'), note ? { note } : {});

export const approveStudioRequest = (requestId: number, note?: string) =>
  postJson<StudioRequestResult>(requestPath(requestId, 'approve'), note ? { note } : {});

/** Ghi chú bắt buộc 5–500 ký tự. */
export const rejectStudioRequest = (requestId: number, note: string) =>
  postJson<StudioRequestResult>(requestPath(requestId, 'reject'), { note });

/** "Thử ghi lại" (Q8), không có thân. */
export const applyStudioRequest = (requestId: number) =>
  postJson<StudioRequestResult>(requestPath(requestId, 'apply'), {});

/** Q7: chỉ Đồng ý, cùng một `kind`, 1–20 id. */
export const bulkApproveStudioRequests = (body: {
  kind: StudioQueueKind;
  ids: number[];
  note?: string;
}) => postJson<StudioBulkResponse>(STUDIO_URLS.studioRequestsBulk, body);

// --------------------------------------------- đơn của thành viên (/approvals/*) ---

export const getApprovalsMe = () => getJson<ApprovalMe>(STUDIO_URLS.approvalsMe);

export const getApprovalsSummary = (studioId?: number) =>
  getJson<ApprovalSummary>(STUDIO_URLS.approvalsSummary, { studioId });

export const getApprovalsQueue = (q: ApprovalsQueueQuery) =>
  getJson<Paged<QueueItem>>(STUDIO_URLS.approvalsQueue, { ...q });

export const bulkApprovals = (body: { kind: ApprovalKind; ids: number[]; note?: string }) =>
  postJson<BulkResponse>(STUDIO_URLS.approvalsBulk, body);

/** Duyệt / từ chối một đơn LEAVE hoặc CLAIM bằng route sẵn có (mục 8.7.8). */
export function reviewMemberItem(
  kind: 'LEAVE' | 'CLAIM',
  id: number,
  approve: boolean,
  note?: string
): Promise<unknown> {
  const action = approve ? 'approve' : 'reject';
  if (kind === 'LEAVE') {
    return patchJson(`${API.leaveRequests}/${id}/${action}`, note ? { reviewNote: note } : {});
  }
  return patchJson(`${API.starsMissionReviews}/${id}/${action}`, note ? { note } : {});
}

// ------------------------------------------------------------- tải ảnh ---

/**
 * Tải một ảnh lên hạ tầng sẵn có (POST /image/storages, trường 'attachment') và trả KHOÁ 'hp/<uuid>' —
 * giá trị duy nhất mà `evidenceUrl` (S24) và `avatar` (S4, S6) nhận. Lỗi thì ném (khác uploadFileFormData
 * của service/common.ts vốn nuốt lỗi).
 */
export async function uploadStudioImage(file: File | Blob): Promise<string> {
  const form = new FormData();
  form.append('attachment', file);
  const res: any = await AxiosUtils.post(API.uploadFile, form, {
    'Content-Type': 'multipart/form-data',
  });
  const key = res?.data?.url;
  if (typeof key !== 'string' || !key) {
    throw clientError('UPLOAD_FAILED', 'Tải ảnh lên không thành công, vui lòng thử lại.');
  }
  return key;
}

// ------------------------------------------------------------ người xem ---

// GET /studios/mine gọi MỘT lần cho mỗi người dùng. Khoá theo userId (từ auth/status qua useCurrentRoles),
// KHÔNG theo accessToken: backend luôn trả chuỗi "OK" cho mọi lần đăng nhập nên đổi tài khoản trong cùng tab
// vẫn cùng khoá (xem hooks/use-current-roles.ts). Không biết userId (auth/status lỗi) thì hỏi thẳng, không nhớ.
// Lỗi (backend chưa có route → 404, mất mạng) không nhớ, để lần sau hỏi lại.
let mineCache: { userId: number; promise: Promise<StudioMine> } | null = null;

export function loadStudioMine(userId: number | null | undefined, force = false): Promise<StudioMine> {
  if (!userId) {
    mineCache = null;
    return getStudioMine();
  }
  if (!force && mineCache && mineCache.userId === userId) return mineCache.promise;
  const promise = getStudioMine().catch((err) => {
    if (mineCache?.promise === promise) mineCache = null;
    throw err;
  });
  mineCache = { userId, promise };
  return promise;
}

/** Xoá kết quả /studios/mine đã nhớ. Gói I2: resetCurrentUserCache() nên gọi kèm hàm này. */
export function resetStudioMineCache() {
  mineCache = null;
}

export interface StudioViewer {
  loading: boolean;
  /** Lỗi của GET /studios/mine (đọc bằng describeStudioError). */
  error: any | null;
  mine: StudioMine | null;
  kind: StudioViewerKind | null;
  isAdmin: boolean;
  isSmod: boolean;
  /** Quản lý Studio (kind MANAGER). */
  isManager: boolean;
  canViewAll: boolean;
  canAdmin: boolean;
  /** Studio của Quản lý (null với Admin / SMOD). */
  studioId: number | null;
  scopeMode: StudioScopeMode | null;
  reload: () => void;
}

/**
 * Người xem trang Studio, từ GET /studios/mine (mục 10.4: các trang A2–A4 lấy quyền hiện nút ở đây, không
 * dùng useCurrentRoles().audience). Mọi quyết định quyền do server kiểm; phía này chỉ ẩn / hiện.
 */
export function useStudioViewer(): StudioViewer {
  // Chờ auth/status (dùng chung promise đã nhớ của useCurrentRoles, không gọi thêm) để biết userId.
  const { loaded: rolesLoaded, userId } = useCurrentRoles();
  const [mine, setMine] = useState<StudioMine | null>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!rolesLoaded) return undefined;
    let cancelled = false;
    setLoading(true);
    loadStudioMine(userId, tick > 0)
      .then((next) => {
        if (cancelled) return;
        setMine(next);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setMine(null);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rolesLoaded, userId, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const kind = mine?.kind ?? null;
  return {
    loading,
    error,
    mine,
    kind,
    isAdmin: kind === 'ADMIN',
    isSmod: kind === 'SMOD',
    isManager: kind === 'MANAGER',
    canViewAll: !!mine?.canViewAll,
    canAdmin: !!mine?.canAdmin,
    studioId: mine?.studio?.id ?? null,
    scopeMode: mine?.scopeMode ?? null,
    reload,
  };
}

/** Quyền hiện nút trên MỘT Studio (bảng V/T/R/A mục 4.2). */
export interface StudioAbilities {
  /** A: Admin — sửa, đổi Quản lý, xoá, thêm/gỡ/chuyển thành viên, gắn kênh, duyệt đề nghị. */
  admin: boolean;
  /** T: tạo / sửa / lưu trữ Nhóm nhảy, xếp người: Admin, Quản lý của chính Studio. */
  manageTeams: boolean;
  /** R: gửi đề nghị: chỉ Quản lý của chính Studio. */
  request: boolean;
  /** Chỉ xem (SMOD). */
  readOnly: boolean;
  /** Thấy email, cờ, lọc "Chỉ dòng có cờ": Admin, SMOD. */
  seesFlags: boolean;
}

export function studioAbilities(mine: StudioMine | null, studioId: number): StudioAbilities {
  const kind = mine?.kind;
  const ownStudio =
    kind === 'MANAGER' &&
    (mine?.studio?.id === studioId || !!mine?.otherStudioIds?.includes(studioId));
  return {
    admin: kind === 'ADMIN',
    manageTeams: kind === 'ADMIN' || ownStudio,
    request: ownStudio,
    readOnly: kind === 'SMOD',
    seesFlags: kind === 'ADMIN' || kind === 'SMOD',
  };
}

// ------------------------------------------------------------ tải dữ liệu ---

export interface StudioLoad<T> {
  data: T | null;
  /** Lỗi của lần tải gần nhất (đọc bằng describeStudioError); xoá khi bắt đầu lượt tải mới. */
  error: any | null;
  loading: boolean;
  /** Đã xong ít nhất một lần (thành công hoặc lỗi) với `resetKey` hiện tại. */
  settled: boolean;
  reload: () => void;
  setData: (next: T | null) => void;
}

/**
 * Tải dữ liệu cho một bảng / tab: gọi lại khi `deps` đổi (so bằng JSON) hoặc khi `reload()`. Lần tải lại
 * (đổi trang, bộ lọc) giữ dữ liệu cũ (bảng hiện `loading`); lỗi KHÔNG bật thông báo mà trả về `error` để
 * trang hiện Alert + nút "Thử lại" (mục 8.7). Thay cho usePagedList của star-shared (vốn báo lỗi bằng
 * mapStarError).
 * `resetKey` (VD studioId): khi khoá này đổi thì bỏ dữ liệu, lỗi và `settled` của khoá cũ ngay trong lần vẽ
 * đó — cùng component /studio/:id không được dựng lại khi sang Studio khác, không được hiện dòng của Studio cũ.
 */
export function useStudioLoad<T>(
  load: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: { enabled?: boolean; resetKey?: unknown } = {}
): StudioLoad<T> {
  const { enabled = true } = options;
  const resetKey = JSON.stringify(options.resetKey ?? null);
  // Mỗi giá trị nhớ kèm khoá nó thuộc về; khác khoá hiện tại thì coi như chưa có.
  const [data, setDataSlot] = useState<{ key: string; value: T | null }>({ key: resetKey, value: null });
  const [error, setError] = useState<{ key: string; value: any } | null>(null);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const resetKeyRef = useRef(resetKey);
  resetKeyRef.current = resetKey;
  const depsKey = JSON.stringify(deps);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const key = resetKey;
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then((next) => {
        if (cancelled) return;
        setDataSlot({ key, value: next });
      })
      .catch((err) => {
        if (!cancelled) setError({ key, value: err });
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setSettledKey(key);
      });
    return () => {
      cancelled = true;
    };
  }, [depsKey, resetKey, tick, enabled]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const setData = useCallback(
    (next: T | null) => setDataSlot({ key: resetKeyRef.current, value: next }),
    []
  );
  const settled = settledKey === resetKey;
  return {
    data: data.key === resetKey ? data.value : null,
    error: error && error.key === resetKey ? error.value : null,
    // Khoá vừa đổi, lượt tải mới chưa kịp bắt đầu: vẫn báo đang tải.
    loading: enabled && (loading || !settled),
    settled,
    reload,
    setData,
  };
}
